import React, { useEffect, useState } from "react";
import { Spinner, Card, Table, Badge, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import AdminServiceV2 from "../../../../services/admin.service.v2";
import SearchBar from "./SearchBar";
import CenterRow from "./CenterRow";
import toast from "react-hot-toast";
import { convertToCSV, downloadCSV } from "../../../../components/helper/csv.helper";

interface IShiftCount {
    shiftId: string;
    noOfStudents: number;
    shiftName: string;
}

interface ICenter {
    center: string;
    shift_count: IShiftCount[];
}

interface ICenterDataProps {
    examId?: string;
}

export default function CenterDataV2(props: ICenterDataProps) {
    const [centers, setCenters] = useState<ICenter[]>([]);
    const [loading, setLoading] = useState(false);
    const [bLoading, setBLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [invigilatorStatus] = useState<{ [key: string]: boolean }>({});
    const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
    const [searchTerm, setSearchTerm] = useState("");
    const [centerInvigilators, setCenterInvigilators] = useState<any[]>([]);

    const fetchCenters = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await AdminServiceV2.getCenters(props.examId!);
            const data = response.data;
            setCenters(data.data.centers);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch centers");
            console.error("Error fetching centers:", err);
        } finally {
            setLoading(false);
        }
    };

    async function createCenterInv() {
        if (!props.examId) {
            toast.error("Unable to get examId")
            return;
        }

        setBLoading(true);
        await AdminServiceV2.creaetCenterInv(props.examId)
            .then((res) => {
                if (res.status == 200) {
                    console.log(res.data);
                }
            })
            .catch((err) => {
                toast.error(err?.response?.data?.message || err?.response?.data || "Something went wrong");
            })
            .finally(() => {
                setBLoading(false);
            });
    }

    async function getCenterInvList() {
        if (!props.examId) {
            toast.error("Unable to get examId")
            return;
        }
        await AdminServiceV2.getCenterInvList(props.examId)
            .then((res) => {
                if (res.status == 200) {
                    setCenterInvigilators(res?.data?.data?.center_inv || []);
                }
            });
    }

    const getTotalStudents = (center: ICenter) => {
        return center.shift_count.reduce((sum, shift) => sum + shift.noOfStudents, 0);
    };

    const toggleRow = (centerId: string) => {
        setExpandedRows((prev) => ({
            ...prev,
            [centerId]: !prev[centerId]
        }));
    };

    function downloadCenterInvigilatorsCSV() {
        let csvData = convertToCSV(centerInvigilators?.map((inv) => {
            return {
                centerId: inv.centerId,
                centerInvId: inv.centerInvId,
                plainPassword: inv.plainPassword,
                userName: inv.userName
            }
        }));
        downloadCSV(csvData, "centerInvigilators.csv");
    }

    useEffect(() => {
        if (props.examId) {
            fetchCenters();
            getCenterInvList();
        }
    }, [props.examId]);

    if (!props.examId) {
        return <div className="text-center text-muted">Please provide an exam ID</div>;
    }

    const filteredCenters = centers.filter((c) => c.center.toLowerCase().includes(searchTerm.toLowerCase()));
    const invMap: { [centerId: string]: any } = {};
    centerInvigilators.forEach((ci) => {
        if (ci.centerId) invMap[ci.centerId] = ci;
    });
    return (
        <>
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="text-center">
                        <Spinner animation="border" role="status" className="mb-3" variant="primary">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="text-muted mt-2">Loading center data...</p>
                    </div>
                </div>
            ) : error ? (
                <Card className="border-danger bg-light">
                    <Card.Body>
                        <div className="alert alert-danger mb-0" role="alert">
                            <strong>Error!</strong> {error}
                        </div>
                    </Card.Body>
                </Card>
            ) : centers.length > 0 ? (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h5 className="fw-bold text-dark mb-1">
                                <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
                                Centers Overview
                            </h5>
                            <p className="text-muted small mb-0">Total Centers: <strong>{centers.length}</strong></p>
                        </div>
                        <div className="d-flex align-items-center">
                            <Button variant="outline-primary" className="fw-bold me-3" size="sm" onClick={createCenterInv} disabled={bLoading}>
                                {!bLoading ? "Create Center Invigilators" : "Loading..."}
                            </Button>
                            <Button variant="outline-secondary" className="fw-bold me-3" size="sm" onClick={downloadCenterInvigilatorsCSV}>
                                Download Center Invigilators
                            </Button>
                        </div>
                    </div>

                    <SearchBar value={searchTerm} onChange={setSearchTerm} />

                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover bordered className="mb-0" style={{ minWidth: '800px' }}>
                                    <thead >
                                        <tr>
                                            <th className="py-3 px-4 text-muted fw-semibold" style={{ width: '40px' }}></th>
                                            <th className="py-3 px-4 text-muted fw-semibold">Center Code</th>
                                            <th className="py-3 px-4 text-muted fw-semibold">Total Students</th>
                                            <th className="py-3 px-4 text-muted fw-semibold">Shifts</th>
                                            <th className="py-3 px-4 text-muted fw-semibold text-center">Invigilator</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCenters.map((center, index) => {
                                            const totalStudents = getTotalStudents(center);
                                            const isExpanded = expandedRows[center.center];
                                            const inv = invMap[center.center];

                                            return (
                                                <React.Fragment key={center.center}>
                                                    <CenterRow
                                                        center={center}
                                                        index={index}
                                                        totalStudents={totalStudents}
                                                        isExpanded={!!isExpanded}
                                                        toggleRow={toggleRow}
                                                        hasInvigilators={!!inv}
                                                        invigilator={inv}
                                                    />
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </>
            ) : (
                <Card className="border-0 shadow-sm text-center py-5">
                    <Card.Body>
                        <FontAwesomeIcon icon={faBuilding} size="3x" className="text-muted mb-3 d-block" />
                        <h6 className="text-muted fw-bold">No Centers Available</h6>
                        <p className="text-muted small">No center data found for this exam.</p>
                    </Card.Body>
                </Card>
            )}
        </>
    );
}